import React, { useEffect, useState, useMemo } from 'react';
/* global axios */
import api from '../services/apiService';
import { formatDateDE } from '../utils/dateFormatter';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';  // Import von React Helmet
import Select from 'react-select'; // Neu: react-select importieren

const InvoiceList = () => {
   // React Router Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlInvoiceNumber = urlParams.get('invoiceNumber');

  // State-Hooks
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [mailModalInvoice, setMailModalInvoice] = useState(null);
  const [selectedRecipientType, setSelectedRecipientType] = useState('invoiceRecipient');
  const [isSendingInvoiceMail, setIsSendingInvoiceMail] = useState(false);
  

  // Initialisierung des Filter-States (mit storedFilters) – "status" wurde durch "bezahlt" ersetzt
const [filters, setFilters] = useState(() => {
    const storedFilters = localStorage.getItem('invoiceListFilters');
    const init = storedFilters
      ? JSON.parse(storedFilters)
      : {
          invoiceNumber: null,
          referenceNumber: null,
          customer: null,
          contact: null,
          paymentState: null,
          date: null,
          invoiceType: null,
        };
    // Wenn Rechnungsnummer per URL mitgegeben, vorab setzen
    if (urlInvoiceNumber) {
      init.invoiceNumber = {
        value: urlInvoiceNumber,
        label: urlInvoiceNumber
      };
    }
    return init;
  });

useEffect(() => {
  localStorage.setItem('invoiceListFilters', JSON.stringify(filters));
}, [filters]);

// State für serverseitige Filteroptionen
const [serverFilterOptions, setServerFilterOptions] = useState({
  invoiceNumbers: [],
  customers: [],
  contacts: [],
  dates: [],
  orderNumbers: [],
  // Der Status-Filter wird hier nicht mehr genutzt, stattdessen könnten statische "bezahlt" Optionen gesetzt werden:
  paid: [
    { value: "true", label: "Bezahlt" },
    { value: "false", label: "Nicht bezahlt" }
  ],
  invoiceTypes: [],
  paymentStates: [],
});

useEffect(() => {
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/invoices/filter-options');
      setServerFilterOptions(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Filteroptionen:', error);
    }
  };
  fetchFilterOptions();
}, []);

// Pagination-Zustände hinzufügen:
const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const limit = 25;

const buildQuery = () => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('page', currentPage);
  if (filters.invoiceNumber) {
    params.append('invoiceNumber', filters.invoiceNumber.value);
  }
  if (filters.orderNumber) { // <- NEU!
    params.append('orderNumber', filters.orderNumber.value);
  }
  if (filters.referenceNumber) {
    params.append('referenceNumber', filters.referenceNumber.value);
  }
  if (filters.customer) {
    params.append('customer', filters.customer.value);
  }
  if (filters.contact) {
    params.append('contact', filters.contact.value);
  }
  if (filters.date) {
    params.append('date', filters.date.value);
  }
  // Statt status wird jetzt "bezahlt" übergeben:
   if (filters.paymentState) {
    params.append('paymentState', filters.paymentState.value); // unpaid | partial | paid
  }
  if (filters.invoiceType) {
    params.append('invoiceType', filters.invoiceType.value);
  }
  return params.toString() ? `?${params.toString()}` : '';
};

useEffect(() => {
  const fetchInvoices = async () => {
    try {
      const query = buildQuery();
      const response = await api.get(`/invoices/list${query}`);
      // Erst die gefilterten Invoices holen
      const fetched = response.data.data;

      // Dann zu jedem Invoice den Kontakt abrufen
      const withContacts = await Promise.all(
        fetched.map(async inv => {
          if (!inv.customerId) return inv;
          try {
            const contactsResp = await api.get(`/contacts/customer/${inv.customerId}`);
            const contacts = Array.isArray(contactsResp.data) ? contactsResp.data : [];
            const ct = contacts.find(c => c.id.toString() === inv.contactId?.toString()) || null;
            const invoiceRecipient = contacts.find(c =>
              (c.contactFunction || '').trim().toLowerCase() === 'rechnungsempfang' &&
              c.contactEmail
            ) || null;
            return { ...inv, contact: ct, invoiceRecipient };
          } catch {
            return { ...inv, contact: null, invoiceRecipient: null };
          }
        })
      );
      setInvoices(withContacts);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Fehler beim Abrufen der Rechnungen:", error);
    }
  };
  fetchInvoices();
}, [filters, currentPage]);

  // ⬇️ Geldbeträge formatieren
const money = (v) => (Number(v || 0).toFixed(2)).replace('.', ',') + ' €';

const PaymentStateBadge = ({ state }) => {
  const map = {
    unpaid: { bg: '#dc3545', txt: 'Unbezahlt' },
    partial: { bg: '#ffc107', txt: 'Teilzahlung' },
    paid: { bg: '#28a745', txt: 'Bezahlt' }
  };
  const item = map[state] || { bg: '#6c757d', txt: state || '-' };
  return (
    <span style={{ background: item.bg, color: '#fff', padding: '3px 8px', borderRadius: '6px', fontSize: 12 }}>
      {item.txt}
    </span>
  );
};

  const handleViewPDF = (invoiceId) => {
    // Erstelle die URL zum Abrufen des PDFs
    const pdfUrl = `${api.defaults.baseURL}/documents/invoice/${invoiceId}/pdf`;
    // Öffne die URL in einem neuen Fenster/Tab
    window.open(pdfUrl, '_blank');
  };
  

  // Funktion zum Erstellen einer Gutschrift aus einer Rechnung
  const createCreditNote = async (invoiceId) => {
    try {
      // Sende POST-Anfrage an den entsprechenden Endpoint
      const response = await api.post(`/creditnotes/duplicate/${invoiceId}`);
      alert('Gutschrift wurde erfolgreich erstellt.');
      // Optional: Navigiere zur Detailansicht der Gutschrift, falls benötigt:
      // navigate(`/creditnotes/${response.data.creditNoteId}`);
    } catch (error) {
      console.error("Fehler beim Erstellen der Gutschrift:", error);
      // Hier die Backend-Meldung extrahieren und anzeigen
    const backendMsg = error.response?.data?.error;
    alert(backendMsg || "Fehler beim Erstellen der Gutschrift.");
    }
  };



 
  

  
  


  // Falls die Customer-Daten nicht in der Rechnung enthalten sind,
  // können diese separat geladen und später via Lookup hinzugefügt werden.
  const fetchCustomers = async () => {
    try {
      const response = await api.get("/customers");
      setCustomers(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Kunden:", error);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Helper-Funktion: Finde den Kunden zu einer Rechnung anhand der customerId
  const getCustomerName = (invoice) => {
    // Falls die Rechnung bereits ein 'customer'-Objekt enthält, nutze dieses
    if (invoice.customer) {
      // In deinem Fall könnte es sein, dass der Name in 'company' steht
      return invoice.customer.company;
    }
    // Falls das 'customer'-Objekt nicht vorhanden ist, versuchen wir per Lookup
    const found = customers.find((c) => c.id === invoice.customerId);
    return found ? found.company : "Unbekannt";
  };

  const getOrderNumber = (invoice) => {
    if (!orders || !Array.isArray(orders)) {
      return '-';
    }
    const order = orders.find(o => o.id === invoice.orderId);
    return order ? order.orderNumber : '-';
  };


  // Rechnung löschen
  const deleteInvoice = (id) => {
    if (window.confirm('Möchten Sie diese Rechnung wirklich löschen?')) {
      api.delete(`/invoices/${id}`)
        .then(() => {
          setInvoices((prev) => prev.filter((inv) => inv.id !== id));
          alert('Rechnung erfolgreich gelöscht!');
        })
        .catch((error) => console.error('Fehler beim Löschen der Rechnung:', error));
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await api.get("/orders");
        setOrders(response.data);
      } catch (error) {
        console.error("Fehler beim Laden der Aufträge:", error);
      }
    };
  
    fetchOrders();
  }, []);

  // Falls du einen Draft (z. B. "Rechnung erstellen als Entwurf") erstellen möchtest,
  // kannst du auch einen entsprechenden Button hinzufügen.
  const createDraftInvoice = async () => {
    try {
      // Hier als Beispiel: Mindestens ein Kunde sollte gesetzt sein.
      // Anpassen nach deinen Anforderungen.
      const payload = {
        customerId: 1, // Beispielhafte Kunden-ID
        status: "draft"
      };

      const response = await api.post('/invoices/draft', payload);
      const { editUrl } = response.data;
      navigate(editUrl); // Navigiert zur Bearbeitungsseite des neu erstellten Entwurfs
    } catch (error) {
      console.error('Fehler beim Erstellen eines Rechnungsentwurfs:', error);
      alert('Fehler beim Erstellen des Rechnungsentwurfs!');
    }
  };

   const formatContactName = (contact) => {
    if (!contact) return 'Nicht vorhanden';
    const name = `${contact.contactFirstName || ''} ${contact.contactLastName || ''}`.trim();
    return name || `Kontakt #${contact.id}`;
  };

   // Hilfsfunktion: Rechnung an den ausgewählten Empfänger mailen
   const sendInvoiceMail = async (invoiceId, recipientType = 'buyer') => {
    try {
      await api.post(`/documents/invoice/${invoiceId}/mail`, { recipientType });
      alert('Rechnung erfolgreich gemailt!');
      return true;
   } catch (error) {
      console.error('Fehler beim Mailen der Rechnung:', error);
      alert(error.response?.data?.message || 'Fehler beim Mailen der Rechnung!');
      return false;
    }
  };

  const handleInvoiceMailClick = (invoice) => {
    if (invoice.invoiceRecipient) {
      setMailModalInvoice(invoice);
      setSelectedRecipientType('invoiceRecipient');
      return;
    }

    sendInvoiceMail(invoice.id, 'buyer');
  };

  const handleConfirmInvoiceMail = async () => {
    if (!mailModalInvoice || isSendingInvoiceMail) return;
    setIsSendingInvoiceMail(true);
    try {
      const sent = await sendInvoiceMail(mailModalInvoice.id, selectedRecipientType);
      if (sent) setMailModalInvoice(null);
    } finally {
      setIsSendingInvoiceMail(false);
    }
  };


// Helper-Funktion: Konvertiere Datum im Format TT.MM.JJJJ in ISO-Format (YYYY-MM-DD)
const convertToISO = (dateStr) => {
  const parts = dateStr.split('.');
  if (parts.length !== 3) return null;
  // parts[0] = TT, parts[1] = MM, parts[2] = JJJJ
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

  // Neue Funktion: Zahlungsdatum setzen
  const handleUpdatePaidDate = async (invoice) => {
    // Standardwert im Format TT.MM.JJJJ, falls vorhanden
    const defaultDate = invoice.paidDate ? formatDateDE(invoice.paidDate) : "";
    const input = window.prompt(
      "Geben Sie das Zahlungsdatum ein (TT.MM.JJJJ). Lassen Sie das Feld leer, um das Datum zu löschen:",
      defaultDate
    );
    if (input === null) return; // Abbruch, wenn Benutzer Abbrechen klickt
  
    const trimmed = input.trim();
    let newDate = trimmed === "" ? null : trimmed;
    
    // Minimaler Format-Check: TT.MM.JJJJ
    if (newDate && !/^\d{2}\.\d{2}\.\d{4}$/.test(newDate)) {
      alert("Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ ein.");
      return;
    }
    
    // Wenn ein Datum eingegeben wurde, konvertiere es ins ISO-Format
    if (newDate) {
      const isoDate = convertToISO(newDate);
      if (!isoDate) {
        alert("Ungültiges Datum.");
        return;
      }
      newDate = isoDate;
    }
    
    try {
      // PUT-Anfrage zum Aktualisieren des Zahlungsdatums (newDate ist entweder ein ISO-String oder null)
      await api.put(`/invoices/${invoice.id}`, { paidDate: newDate });
      // Lokalen State aktualisieren
      setInvoices(prev =>
        prev.map(inv => (inv.id === invoice.id ? { ...inv, paidDate: newDate } : inv))
      );
      alert("Zahlungsdatum aktualisiert.");
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Zahlungsdatums:", error);
      alert("Fehler beim Aktualisieren des Zahlungsdatums.");
    }
  };

  return (
    <div className="invoice-list">
      <Helmet>
        <title>Rechnungliste</title>
        <meta name="description" content="Auftragliste" />
      </Helmet>
      <h1>Rechnungen</h1>

      {/* Button zum Erstellen eines neuen Rechnungsentwurfs */}

      {/* Filterleiste oberhalb der Tabelle */}
<div style={{ marginBottom: '20px' }}>
  <h3>Filter</h3>
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px' }}>
  <Select
  options={
    (serverFilterOptions.invoiceNumbers || [])
      .slice()
      .sort((a, b) => {
        const aNum = parseInt(a.replace(/\D/g, ''), 10);
        const bNum = parseInt(b.replace(/\D/g, ''), 10);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return bNum - aNum; // numerisch absteigend
        }
        return b > a ? 1 : -1; // alphabetisch absteigend (Fallback)
      })
      .map(val => ({ value: val, label: val }))
  }
  placeholder="Rechnungsnummer"
  isClearable
  value={filters.invoiceNumber}
  onChange={selected => setFilters({ ...filters, invoiceNumber: selected })}
  styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
/>
    <Select
  options={
    (serverFilterOptions.orderNumbers || [])
      .slice() // Kopie, um Original nicht zu verändern
      .sort((a, b) => {
        // Falls reine Zahlen: numerisch sortieren
        // Falls gemischt: als String
        const aNum = parseInt(a.replace(/\D/g, ''), 10);
        const bNum = parseInt(b.replace(/\D/g, ''), 10);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return bNum - aNum;
        }
        // Fallback: alphabetisch absteigend
        return b > a ? 1 : -1;
      })
      .map(v => ({ value: v, label: v }))
  }
  placeholder="Auftragsnr."
  isClearable
  value={filters.orderNumber}
  onChange={sel => setFilters(f => ({ ...f, orderNumber: sel }))}
/>
    <Select
      options={serverFilterOptions.customers.map(val => ({ value: val, label: val }))}
      placeholder="Kunde"
      isClearable
      value={filters.customer}
      onChange={(selected) => setFilters({ ...filters, customer: selected })}
      styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
    />
    <Select
      options={serverFilterOptions.contacts.map(val => ({ value: val, label: val }))}
      placeholder="Besteller"
      isClearable
      value={filters.contact}
      onChange={(selected) => setFilters({ ...filters, contact: selected })}
      styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
    />
    {/* ⬇️ NEU: Zahlungsstatus */}
    <Select
      options={[
        { value: 'unpaid',  label: 'Unbezahlt' },
        { value: 'partial', label: 'Teilzahlung' },
        { value: 'paid',    label: 'Bezahlt' },
      ]}
      placeholder="Zahlungsstatus"
      isClearable
      value={filters.paymentState}
      onChange={(selected) => setFilters({ ...filters, paymentState: selected })}
      styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
    />
    <Select
      options={serverFilterOptions.dates.map(val => ({ value: val, label: val }))}
      placeholder="Rechnungsdatum"
      isClearable
      value={filters.date}
      onChange={(selected) => setFilters({ ...filters, date: selected })}
      styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
    />
    
    <Select
      options={serverFilterOptions.invoiceTypes.map(val => ({ value: val, label: val }))}
      placeholder="Rechnungstyp"
      isClearable
      value={filters.invoiceType}
      onChange={(selected) => setFilters({ ...filters, invoiceType: selected })}
      styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
    />
  </div>
</div>

      

      {invoices.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Rechnungsnummer</th>
              <th>Kunde</th>
              <th>Besteller</th>
              <th>Rechnungsdatum</th>
              <th>Einsatzort</th>
    {/*       <th>Gesamt netto (€)</th>  */}
              <th>Gesamt brutto (€)</th>
              <th>Bereits bezahlt (€)</th> 
              <th>Zahlungsstatus</th>      
              <th>Letzte Zahlung</th>      
              <th>Auftragsnummer</th>
              <th>Rechnungstyp</th>
              <th>Aktionen</th>
              <th>Ausgabe</th>
              <th>Gutschrift</th>
            </tr>
          </thead>
          <tbody>
          {invoices.map((invoice) => {
    const updatedByValid = !!invoice.updatedBy;
    return (
              <tr key={invoice.id}>
                <td>{invoice.id}</td>
                <td>{invoice.invoiceNumber || '-'}</td>
                <td>{getCustomerName(invoice)}</td>
                <td>
                  {invoice.contact 
                    ? `${invoice.contact.contactFirstName} ${invoice.contact.contactLastName}`
                    : 'Kein Besteller'}
                </td>
                <td>{formatDateDE(invoice.date)}</td>
                <td>{invoice.operationLocation || 'Nicht angegeben'}</td>
                
        {/*     <td>{(invoice.totalNet?.toFixed(2).replace('.', ',') || '0,00') + ' €'}</td>        */}
                <td>{money(invoice.totalGross)}</td> 
                <td>{money(invoice.amountPaid)}</td>  
                <td
  onClick={() => navigate(`/paymentform?invoiceId=${invoice.id}`)}
  title="Zahlungen zu dieser Rechnung öffnen"
  style={{ cursor: 'pointer', textAlign: 'center' }}
>
  <PaymentStateBadge state={invoice.paymentState} />
</td>
                <td>{invoice.lastPaymentDate ? formatDateDE(invoice.lastPaymentDate) : '-'}</td>
                <td>{getOrderNumber(invoice)}</td>
                <td>{invoice.invoiceType}</td>
                 
                <td>
        {invoice.invoiceType !== 'downPayment' && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button
              onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
              style={{
                fontSize: '15px',
                padding: '5px 10px',
                background: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                marginRight: '10px',
                cursor: 'pointer',
              }}
            >
              Bearbeiten
            </button>
            <button
              onClick={() => deleteInvoice(invoice.id)}
              style={{
                fontSize: '15px',
                padding: '5px 10px',
                background: '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Löschen
            </button>
          </div>
        )}
      </td>
      <td>
  {updatedByValid ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <button
        onClick={() => handleViewPDF(invoice.id)}
        style={{
          fontSize: '15px',
          padding: '5px 10px',
          background: '#28a745',
          color: '#fff',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
      >
        Ansicht
      </button>
      <button
        onClick={() => handleInvoiceMailClick(invoice)}
        style={{
          fontSize: '15px',
          padding: '5px 10px',
          background: '#17a2b8',
          color: '#fff',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
      >
        Mailen
      </button>
    </div>
  ) : (
    <div style={{ color: '#aaa', fontStyle: 'italic' }}>
      <span>Ausgabe nach Speicherung möglich</span>
    </div>
  )}
</td>

      <td>
      {updatedByValid ? (
    invoice.creditNoteCreated ? null : (
  <button
    onClick={() => createCreditNote(invoice.id)}
    style={{
      fontSize: '15px',
      padding: '5px 10px',
      background: '#28a745',
      color: '#fff',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
    }}
  >
    Gutschrift erstellen
  </button>
 )
  ) : (
    <div style={{ color: '#aaa', fontStyle: 'italic' }}>
      <span>Gutschrift nach Speicherung möglich</span>
    </div>
  )}
</td>


    </tr>
    );
})}
</tbody>
        </table>
      ) : (
        <p>Keine Rechnungen verfügbar.</p>
      )}
{mailModalInvoice && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
    }}
  >
    <div
      style={{
        width: 'min(520px, calc(100vw - 32px))',
        background: '#fff',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.25)',
      }}
    >
      <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '22px' }}>
        Rechnung senden an
      </h2>

      <label
        style={{
          display: 'block',
          border: selectedRecipientType === 'buyer' ? '2px solid #17a2b8' : '1px solid #ddd',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '10px',
          cursor: 'pointer',
        }}
      >
        <input
          type="radio"
          name="invoiceRecipientType"
          value="buyer"
          checked={selectedRecipientType === 'buyer'}
          onChange={() => setSelectedRecipientType('buyer')}
          style={{ marginRight: '8px' }}
        />
        <strong>Besteller</strong>
        <div style={{ marginLeft: '24px', marginTop: '6px', lineHeight: 1.4 }}>
          <div>{formatContactName(mailModalInvoice.contact)}</div>
          <div>{mailModalInvoice.contact?.contactEmail || 'Keine E-Mail hinterlegt'}</div>
        </div>
      </label>

      <label
        style={{
          display: 'block',
          border: selectedRecipientType === 'invoiceRecipient' ? '2px solid #17a2b8' : '1px solid #ddd',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '18px',
          cursor: 'pointer',
        }}
      >
        <input
          type="radio"
          name="invoiceRecipientType"
          value="invoiceRecipient"
          checked={selectedRecipientType === 'invoiceRecipient'}
          onChange={() => setSelectedRecipientType('invoiceRecipient')}
          style={{ marginRight: '8px' }}
        />
        <strong>Rechnungsempfang</strong>
        <div style={{ marginLeft: '24px', marginTop: '6px', lineHeight: 1.4 }}>
          <div>{formatContactName(mailModalInvoice.invoiceRecipient)}</div>
          <div>{mailModalInvoice.invoiceRecipient?.contactEmail || 'Keine E-Mail hinterlegt'}</div>
        </div>
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button
          type="button"
          onClick={() => setMailModalInvoice(null)}
          disabled={isSendingInvoiceMail}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            background: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: isSendingInvoiceMail ? 'not-allowed' : 'pointer',
          }}
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleConfirmInvoiceMail}
          disabled={isSendingInvoiceMail}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            background: '#17a2b8',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: isSendingInvoiceMail ? 'not-allowed' : 'pointer',
          }}
        >
          {isSendingInvoiceMail ? 'Senden...' : 'Senden'}
        </button>
      </div>
    </div>
  </div>
)}
<div style={{ marginTop: '1rem' }}>
  <button
    onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
    disabled={currentPage === 1}
    style={{
      fontSize: '15px',
      padding: '8px 12px',
      backgroundColor:'rgb(155, 155, 155)',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer'
    }} 
  >
    Vorherige
  </button>
  <span style={{ margin: '0 1rem' }}>
    Seite {currentPage} von {totalPages}
  </span>
  <button
    onClick={() => setCurrentPage(currentPage + 1)}
    disabled={currentPage >= totalPages}
    style={{
      fontSize: '15px',
      padding: '8px 12px',
      backgroundColor:'rgb(155, 155, 155)',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer'
    }} 
  >
    Nächste
  </button>
</div>
      {/* Alternativ: Button unten erneut */}
      
    </div>
  );
};

export default InvoiceList;
